import React, { useState, useEffect } from 'react';
import '../../styles/components.css';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'destructive';

export interface ToastProps {
  /**
   * Toast variant
   * @default 'default'
   */
  variant?: ToastVariant;
  
  /**
   * Toast message content
   */
  message?: React.ReactNode;
  
  /**
   * Toast description (alternative to message)
   */
  description?: React.ReactNode;
  
  /**
   * Optional toast title
   */
  title?: React.ReactNode;
  
  /**
   * Optional icon to display
   */
  icon?: React.ReactNode;
  
  /**
   * Duration in milliseconds before auto-dismissing
   * @default 3000
   */
  duration?: number;
  
  /**
   * Whether the toast can be dismissed manually
   * @default true
   */
  dismissible?: boolean;
  
  /**
   * Callback for when the toast is dismissed
   */
  onDismiss?: () => void;
  
  /**
   * Position of the toast
   * @default 'bottom-right'
   */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  
  /**
   * Children elements
   */
  children?: React.ReactNode;
}

/**
 * Toast component for transient notifications
 */
export const Toast: React.FC<ToastProps> = ({
  variant = 'default',
  message,
  description,
  title,
  icon,
  duration = 3000,
  dismissible = true,
  onDismiss,
  position = 'bottom-right',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  
  // Use message or description, preferring message if both are provided
  const content = message || description;
  
  useEffect(() => {
    if (duration !== Infinity) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onDismiss) onDismiss();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, onDismiss]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };
  
  if (!isVisible) return null;
  
  // Compute position class
  const getPositionClass = () => {
    switch (position) {
      case 'top-right':
        return 'fixed top-4 right-4';
      case 'top-left':
        return 'fixed top-4 left-4';
      case 'bottom-left':
        return 'fixed bottom-4 left-4';
      case 'bottom-right':
      default:
        return 'fixed bottom-4 right-4';
    }
  };
  
  // Generate variant-specific classes
  const getVariantClass = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-100 text-green-800 border-l-4 border-green-500';
      case 'error':
        return 'bg-red-100 text-red-800 border-l-4 border-red-500';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-l-4 border-blue-500';
      default:
        return 'toast';
    }
  };
  
  return (
    <div 
      className={`${getPositionClass()} ${getVariantClass()} shadow-lg rounded-lg overflow-hidden max-w-sm w-full`}
      role="alert"
      aria-live="assertive"
    >
      <div className="p-4 flex">
        {icon && (
          <div className="flex-shrink-0 mr-3">
            {icon}
          </div>
        )}
        
        <div className="flex-1">
          {title && (
            <div className="font-semibold mb-1">{title}</div>
          )}
          <div>{content}</div>
        </div>
        
        {dismissible && (
          <button
            type="button"
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-500"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Toast Container Component
export interface ToastContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  children: React.ReactNode;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  position = 'bottom-right',
  children,
}) => {
  // Compute position class
  const getPositionClass = () => {
    switch (position) {
      case 'top-right':
        return 'fixed top-4 right-4';
      case 'top-left':
        return 'fixed top-4 left-4';
      case 'bottom-left':
        return 'fixed bottom-4 left-4';
      case 'bottom-right':
      default:
        return 'fixed bottom-4 right-4';
    }
  };
  
  return (
    <div 
      className={`${getPositionClass()} flex flex-col gap-2 z-50`}
      role="region"
      aria-label="Notifications"
    >
      {children}
    </div>
  );
};

export default Toast;

// Add these additional exports for the toaster component
export const ToastClose: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <button
    className={`toast-close ${className}`}
    aria-label="Close"
    {...props}
  >
    {children || (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    )}
  </button>
);

export const ToastDescription: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`toast-description ${className}`} {...props}>
    {children}
  </div>
);

export const ToastTitle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`toast-title ${className}`} {...props}>
    {children}
  </div>
);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

export const ToastViewport: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`toast-viewport ${className}`} {...props}>
    {children}
  </div>
);

export type ToastActionElement = React.ReactElement;